import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPhoneAlt, FaUser, FaIdCard, FaBolt, FaBullhorn, FaBoxOpen, FaHashtag,
         FaCalendarAlt, FaClock, FaSitemap, FaCheckCircle, FaSearch } from 'react-icons/fa';
import { capturarFusion, listarAsesoras, asignarFusion, buscarPorId } from '../../servicios/Fusion';
import './fusion.css';

const VACIO = {
    telefono: '', id_fusion: '', nombre_fusion: '', ci_fusion: '',
    canal: '', campania: '', producto: '',
    fecha_creacion: '', hora_creacion: '',
};

export default function Fusion() {
    const [d, setD] = useState(VACIO);
    const [cargando, setCargando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [asesoras, setAsesoras] = useState([]);
    const [modo, setModo] = useState('auto');
    const [gestorManual, setGestorManual] = useState('');
    const [asignando, setAsignando] = useState(false);
    const [asignado, setAsignado] = useState(null);
    // Gestión previa del número en el CRM: no es un error, es una pregunta al operador.
    const [aviso, setAviso] = useState(null);
    // Qué encontró la búsqueda por Id: la solicitud ya capturada, o nada.
    const [yaEnBitacora, setYaEnBitacora] = useState(null);

    useEffect(() => { listarAsesoras().then(setAsesoras).catch(() => {}); setD((p) => ({ ...p, fecha_creacion: hoyStr() })); }, []);

    /*
     * Cambiar el TELÉFONO después de haber capturado arranca otro lead: los datos de Fusión
     * que quedaron en pantalla son del anterior. Si no se limpian, se guardan con el número
     * nuevo — así se registraron 5 leads con el Id de otro (mismo id, misma hora al segundo,
     * teléfonos distintos). La pantalla solo se limpiaba al ASIGNAR, y quien capturaba sin
     * asignar arrastraba todo.
     * Mientras no haya captura hecha se respeta lo escrito: ahí el operador está corrigiendo
     * un dígito, no cambiando de lead.
     */
    const set = (k) => (e) => {
        const v = e.target.value;
        // Cambiar el ID después de haber capturado arranca OTRA solicitud: lo que quedó en
        // pantalla es de la anterior. Si no se limpia, se guarda con el id nuevo — así se
        // registraron 5 leads con el id de otro.
        if (k === 'id_fusion' && resultado) {
            setD({ ...VACIO, id_fusion: v, fecha_creacion: hoyStr() });
            setResultado(null); setAsignado(null); setAviso(null); setYaEnBitacora(null);
            setModo('auto'); setGestorManual('');
            return;
        }
        setD({ ...d, [k]: v });
    };

    /*
     * Al terminar de escribir el ID, buscarlo en la bitácora. Si la solicitud ya está, se
     * traen TODOS sus datos, el teléfono incluido: el id es lo que la identifica.
     * Si no está, no se toca nada y se cargan los datos a mano.
     *
     * La búsqueda por TELÉFONO ya no autocompleta: con una fila por solicitud, un número que
     * vuelve otro día devuelve la captura más reciente —la de otra solicitud— y le pegaría su
     * id al lead nuevo. Ahora el teléfono solo avisa si ese número ya pasó por acá.
     */
    const onBlurId = async () => {
        const id = d.id_fusion.trim();
        if (id === '') { setYaEnBitacora(null); return; }
        const dat = await buscarPorId(id);
        setYaEnBitacora(dat);
        if (!dat) return;

        const datos = {
            ...d,
            telefono: dat.telefono || d.telefono,
            ci_fusion: dat.ci_fusion || '',
            nombre_fusion: dat.nombre_fusion || '',
            canal: dat.canal || '',
            campania: dat.campania_fusion || '',
            producto: dat.producto || '',
            fecha_creacion: dat.fecha_creacion_fusion || hoyStr(),
            hora_creacion: dat.hora_creacion_fusion || '',
        };
        setD(datos);

        // Salir del campo hace lo mismo que apretar «Verificar y capturar»: con los datos ya
        // cargados, pedir un clic más para saber si el número está en la base de Claro es
        // trabajo de más. Se le pasan los datos porque setD todavía no los reflejó.
        verificar(datos);
    };

    const pad = (x) => String(x).padStart(2, '0');
    const hoyStr = () => { const n = new Date(); return `${n.getFullYear()}-${pad(n.getMonth()+1)}-${pad(n.getDate())}`; };
    // '2026-09-14 13:25:35' -> '14/09/2026'. La hora no aporta acá y alarga la línea.
    const fechaCorta = (v) => {
        const f = String(v || '').slice(0, 10).split('-');
        return f.length === 3 ? `${f[2]}/${f[1]}/${f[0]}` : String(v || '');
    };
    const ahora = () => {
        const n = new Date();
        setD((prev) => ({ ...prev, fecha_creacion: hoyStr(), hora_creacion: `${pad(n.getHours())}:${pad(n.getMinutes())}:${pad(n.getSeconds())}` }));
    };

    const verificar = async (datos = d) => {
        const tel = datos.telefono.replace(/\D/g, '');
        if (tel.length !== 9 && tel.length !== 10) { toast.error('El teléfono debe tener 9 o 10 dígitos.'); return; }
        if (datos.id_fusion.trim() === '') { toast.error('El Id Fusión es obligatorio.'); return; }
        setCargando(true); setResultado(null); setAsignado(null); setModo('auto'); setGestorManual('');
        try {
            const res = await capturarFusion(datos);
            setResultado(res);
            toast.success('Lead capturado y verificado');
        } catch (ex) { toast.error(ex.message); } finally { setCargando(false); }
    };

    const asignar = async (confirmar = false) => {
        if (modo === 'manual' && !gestorManual) { toast.warn('Elige una asesora.'); return; }
        setAsignando(true);
        try {
            const res = await asignarFusion(resultado.id_bitacora, modo, modo === 'manual' ? Number(gestorManual) : undefined, confirmar);
            // El número ya se gestionó antes y no es una venta cerrada: se muestra qué pasó y
            // decide el operador. Reasignar a ciegas le haría llamar sin saber qué le dijeron.
            if (res.header === 'CONFIRMAR') { setAviso(res); return; }
            setAviso(null);
            toast.success(res.mensaje + ' — listo para el siguiente lead');
            nuevo(); // limpia la pantalla automáticamente, sin preguntar
        } catch (ex) { setAviso(null); toast.error(ex.message); } finally { setAsignando(false); }
    };

    const nuevo = () => { setD({ ...VACIO, fecha_creacion: hoyStr() }); setResultado(null); setAsignado(null); setAviso(null); setYaEnBitacora(null); setModo('auto'); setGestorManual(''); };

    // ¿Esta solicitud ya tenía asesora? No impide reasignar —eso lo decide el estado del
    // CRM— pero cambia lo que dice la pantalla: no es lo mismo entregar que mover de manos.
    const yaAsignada = Number(yaEnBitacora?.asignado) === 1;

    const badge = (enc) => {
        if (enc === 'CLARO-PRE') return <span className="fu-badge fu-pre">Cliente Claro · PREPAGO</span>;
        if (enc === 'CLARO-POST') return <span className="fu-badge fu-post">Cliente Claro · POSTPAGO</span>;
        return <span className="fu-badge fu-no">No está en la base de Claro</span>;
    };

    const campo = (Icon, label, key, opts = {}) => (
        <label className={`fu-field ${opts.req ? 'fu-req' : ''}`}>
            <span><Icon /> {label}</span>
            {opts.datalist
                ? <>
                    <input value={d[key]} onChange={set(key)} list={`dl-${key}`} placeholder={opts.ph || 'Elige o escribe…'} disabled={cargando} autoComplete="off" />
                    <datalist id={`dl-${key}`}>{opts.datalist.map((o) => <option key={o} value={o} />)}</datalist>
                  </>
                : <input type={opts.type || 'text'} step={opts.type === 'time' ? '1' : undefined} value={d[key]} onChange={set(key)} onBlur={opts.onBlur} placeholder={opts.ph || ''} disabled={cargando} />}
            {opts.hint && <small>{opts.hint}</small>}
        </label>
    );

    return (
        <div className="fu-wrap">
            <ToastContainer position="top-right" autoClose={4000} />
            <div className="fu-card">
                <div className="fu-head"><FaBolt /> <div><h2>Asignar lead de Fusión</h2>
                    <p>Ingresa los datos del lead de Fusión, verifica si es cliente Claro y asígnalo a una asesora.</p></div></div>

                <div className="fu-body">
                    {/* ---- Columna izquierda: formulario ---- */}
                    <div className="fu-col-form">
                        <label className="fu-field fu-tel fu-req">
                            <span><FaHashtag /> Id Fusión</span>
                            <input value={d.id_fusion} onChange={set('id_fusion')} onBlur={onBlurId} placeholder="Ingresar id fusión" disabled={cargando} autoComplete="off" />
                            <small>Identifica la solicitud. Si ya está en la bitácora, se cargan sus datos solos.</small>
                        </label>

                        <div className="fu-grid">
                            {campo(FaPhoneAlt, 'Teléfono', 'telefono', { req: true, ph: 'Ingresar teléfono', hint: '9 dígitos — el sistema le antepone el 0' })}
                            {campo(FaUser, 'Nombre Fusión', 'nombre_fusion')}
                            {campo(FaIdCard, 'CI Fusión', 'ci_fusion', { hint: 'cédula que envía Fusión' })}
                            {campo(FaSitemap, 'Canal', 'canal', { datalist: ['WEB', 'Google Leads'], hint: 'elige o escribe otro' })}
                            {campo(FaBullhorn, 'Campaña', 'campania', { datalist: ['Evolution-Portabilidad-Pospago-Asistido', 'WebPublica'], hint: 'elige o escribe otra' })}
                            {campo(FaBoxOpen, 'Producto', 'producto', { datalist: ['portabilidad'], hint: 'elige o escribe otro' })}
                            {campo(FaCalendarAlt, 'Fecha creación', 'fecha_creacion', { type: 'date' })}
                            {campo(FaClock, 'Hora creación', 'hora_creacion', { type: 'time' })}
                        </div>

                        <div className="fu-ahora-row">
                            <button type="button" className="fu-ahora" onClick={ahora} disabled={cargando}>
                                <FaClock /> Poner fecha y hora actuales
                            </button>
                        </div>
                        <button className="fu-btn fu-btn--main" onClick={() => verificar()} disabled={cargando}>
                            <FaSearch /> {cargando ? 'Verificando…' : 'Verificar y capturar'}
                        </button>
                    </div>

                    {/* ---- Columna derecha: panel resultado / asignación ---- */}
                    <div className="fu-col-panel">
                        {!resultado ? (
                            <div className="fu-placeholder">
                                <FaSearch />
                                <p>Completa los datos y presiona <strong>Verificar</strong>.<br />Aquí verás si el número es cliente Claro y podrás asignarlo.</p>
                            </div>
                        ) : (
                            <div className="fu-result">
                                <div className="fu-result__head">
                                    <span className="fu-tel-num">{resultado.telefono}</span>
                                    {badge(resultado.encontrado)}
                                </div>

                                {/* UNA sola alerta con todo lo que ya se sabe del número: en qué quedó en
                                    el CRM y si esta solicitud ya se entregó. Iban en dos recuadros —y otra vez
                                    abajo— y se leían como tres problemas distintos siendo el mismo. */}
                                {(resultado.estado_crm || Number(yaEnBitacora?.asignado) === 1) && (
                                    <div className={`fu-estado ${resultado.estado_crm?.bloquea ? 'fu-estado--bloquea' : ''}`}>
                                        <div className="fu-estado__top">
                                            <span>{resultado.estado_crm ? 'Estado en el CRM' : 'Esta solicitud'}</span>
                                            <strong>{resultado.estado_crm ? resultado.estado_crm.estado : 'Ya asignada'}</strong>
                                        </div>
                                        <span className="fu-estado__pie">
                                            {Number(yaEnBitacora?.asignado) === 1
                                                ? `Ya asignada${yaEnBitacora.fecha_asignacion ? ' el ' + fechaCorta(yaEnBitacora.fecha_asignacion) : ''}${yaEnBitacora.asesor ? ' a ' + yaEnBitacora.asesor : ''}`
                                                : `Última gestión${resultado.estado_crm?.asesora ? ' de ' + resultado.estado_crm.asesora : ''}${resultado.estado_crm?.hoy ? ' · hoy' : (resultado.estado_crm?.fecha ? ' · ' + fechaCorta(resultado.estado_crm.fecha) : '')}`}
                                            {resultado.estado_crm?.bloquea ? ' · no se puede reasignar' : ''}
                                        </span>
                                    </div>
                                )}

                                {resultado.datos_claro && (
                                    <div className="fu-datos">
                                        <h4>Datos de Claro</h4>
                                        <div className="fu-datos-grid">
                                            <div><span>Nombre</span><strong>{resultado.datos_claro.nombre || '—'}</strong></div>
                                            <div><span>Cédula</span><strong>{resultado.datos_claro.identificacion || '—'}</strong></div>
                                            <div><span>Tarifa</span><strong>{resultado.datos_claro.tarifa || '—'}</strong></div>
                                            <div><span>Ciudad</span><strong>{resultado.datos_claro.ciudad || '—'}</strong></div>
                                            <div className="fu-datos-full"><span>Plan</span><strong>{resultado.datos_claro.plan || '—'}</strong></div>
                                        </div>
                                    </div>
                                )}

                                {/* Venta cerrada: no se ofrecen los controles, el servidor los rechaza igual.
                                    Y el freno es del NÚMERO, no de esta solicitud: mientras siga con la venta,
                                    ningún Id nuevo de Fusión lo va a poder reasignar tampoco. */}
                                {!asignado && resultado.estado_crm?.bloquea ? (
                                    <div className="fu-asig">
                                        <h4>Nada que reasignar</h4>
                                        <p className="fu-asig__nota">
                                            Este número ya es venta{resultado.estado_crm.asesora ? ` de ${resultado.estado_crm.asesora}` : ''}. Queda cerrado.
                                        </p>
                                        <button className="fu-btn fu-btn--sec" onClick={nuevo}>Capturar otro lead</button>
                                    </div>
                                ) : !asignado ? (
                                    <div className="fu-asig">
                                        <h4>{yaAsignada ? 'Reasignar a' : 'Asignar a'}</h4>
                                        <div className="fu-asig__modo">
                                            <button className={`fu-asig__op ${modo==='auto'?'activo':''}`} onClick={()=>setModo('auto')} disabled={asignando}>
                                                Automática<small>Mercedes Pluas</small></button>
                                            <button className={`fu-asig__op ${modo==='manual'?'activo':''}`} onClick={()=>setModo('manual')} disabled={asignando}>
                                                Manual<small>elegir asesora</small></button>
                                        </div>
                                        {modo==='manual' && (
                                            <select className="fu-asig__sel" value={gestorManual} onChange={(e)=>setGestorManual(e.target.value)} disabled={asignando}>
                                                <option value="">Elige una asesora…</option>
                                                {asesoras.map(a=><option key={a.id_solicitudes} value={a.id_solicitudes}>{a.agent}</option>)}
                                            </select>
                                        )}
                                        {aviso ? (
                                            <div className="fu-aviso">
                                                <h5>Este número ya fue gestionado</h5>
                                                <div className="fu-aviso__grid">
                                                    <div><span>Estado</span><strong>{aviso.estado_previo?.estado || '—'}</strong></div>
                                                    <div><span>Asesora</span><strong>{aviso.estado_previo?.asesora || '—'}</strong></div>
                                                    <div className="fu-datos-full"><span>Última gestión</span>
                                                        <strong>{aviso.estado_previo?.hoy ? 'Hoy' : (aviso.estado_previo?.fecha || '—')}</strong></div>
                                                </div>
                                                <div className="fu-aviso__btns">
                                                    <button className="fu-btn fu-btn--asig" onClick={() => asignar(true)} disabled={asignando}>
                                                        {asignando ? 'Reasignando…' : 'Reasignar igual'}
                                                    </button>
                                                    <button className="fu-btn fu-btn--sec" onClick={() => setAviso(null)} disabled={asignando}>
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button className="fu-btn fu-btn--asig" onClick={() => asignar(false)} disabled={asignando}>
                                                {asignando ? 'Asignando…' : (yaAsignada ? 'Reasignar' : 'Registrar y asignar')}
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="fu-ok">
                                        <FaCheckCircle />
                                        <p>{asignado.mensaje}</p>
                                        <button className="fu-btn fu-btn--sec" onClick={nuevo}>Capturar otro lead</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
